import { inject } from '@angular/core';
import type { ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, filter, map, take, timeout } from 'rxjs/operators';
import {
    BetaReturnService,
    validateBetaReturnPath,
} from '../beta-return.service';
import { UserStore } from '../user';
import { FETCH_TIMEOUT } from './models';

/**
 * Guard for the onboarding route. Allows access only when the user is authenticated
 * but has no profile (404 from /me). Redirects to / if user already exists.
 */
export const onboardingGuard = (route?: ActivatedRouteSnapshot) => {
    const userStore = inject(UserStore);
    const router = inject(Router);
    const betaReturn = inject(BetaReturnService);
    const returnTo = route
        ? validateBetaReturnPath(route.queryParamMap.get('returnTo'))
        : betaReturn.target;

    return userStore.meStatus$.pipe(
        filter((status) => status === 'success' || status === 'error'),
        take(1),
        map((status) => {
            const user = userStore.me();
            const error = userStore.meQuery.error();
            let result: boolean | UrlTree;
            if (status === 'success' && user) {
                result = betaReturn.navigate(returnTo)
                    ? false
                    : router.createUrlTree(['/']);
            } else if (status === 'error' && error?.status === 404) {
                result = true;
            } else {
                result = returnTo
                    ? router.createUrlTree(['/login'], {
                          queryParams: { returnTo },
                      })
                    : router.createUrlTree(['/login']);
            }
            return result;
        }),
        timeout(FETCH_TIMEOUT),
        catchError(() => of(router.createUrlTree(['/service-unavailable'])))
    );
};
